import { useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Edit2, Trash2, AlertTriangle, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';

export function DancesTab({ dances, fetchDances, searchDance, setSearchDance, currentUser }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    tempo: '',
    sequences_required: 1
  });
  const [showPermissionWarning, setShowPermissionWarning] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [viewOnly, setViewOnly] = useState(false);
  
  const isSuperUser = currentUser?.role === 'super';
  
  const resetForm = () => {
    setFormData({ name: '', tempo: '', sequences_required: 1 });
    setEditingItem(null);
    setViewOnly(false);
  };
  
  const handleAdd = () => {
    if (!isSuperUser) {
      setShowPermissionWarning(true);
      return;
    }
    resetForm();
    setDialogOpen(true);
  };
  
  const handleEdit = (dance) => {
    if (!isSuperUser) {
      setShowPermissionWarning(true);
      return;
    }
    setFormData({
      name: dance.name,
      tempo: dance.tempo,
      sequences_required: dance.sequences_required
    });
    setEditingItem(dance);
    setViewOnly(false);
    setDialogOpen(true);
  };

  const handleView = (dance) => {
    setFormData({
      name: dance.name,
      tempo: dance.tempo,
      sequences_required: dance.sequences_required
    });
    setEditingItem(dance);
    setViewOnly(true);
    setDialogOpen(true);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axios.patch(`${API}/content/compulsory-dances/${editingItem.dance_id}`, formData, { withCredentials: true });
        toast.success('Dance updated!');
      } else {
        await axios.post(`${API}/content/compulsory-dances`, formData, { withCredentials: true });
        toast.success('Dance created!');
      }
      setDialogOpen(false);
      resetForm();
      fetchDances();
    } catch (error) {
      toast.error('Failed to save dance');
    }
  };
  
  const handleDeleteClick = (id) => {
    if (!isSuperUser) {
      setShowPermissionWarning(true);
      return;
    }
    const dance = dances.find(d => d.dance_id === id);
    setItemToDelete(dance);
    setDeleteDialogOpen(true);
  };
  
  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/content/compulsory-dances/${itemToDelete.dance_id}`, { withCredentials: true });
      toast.success('Dance deleted');
      fetchDances();
    } catch (error) {
      toast.error('Failed to delete dance');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };
  
  return (
    <>
      <div className="flex justify-between items-center mb-4 gap-4">
        <Input 
          placeholder="Search dances..." 
          value={searchDance} 
          onChange={(e) => setSearchDance(e.target.value)}
          data-testid="dance-search-input"
          className="max-w-xs"
        />
        <Button onClick={handleAdd} className="btn-primary" data-testid="add-dance-button">
          <Plus className="mr-2 h-4 w-4" />
          Add Dance
        </Button>
      </div>
      
      {!isSuperUser && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
          Compulsory Dances can only be modified by Super Users. You can view all dances and use them in your grades.
        </div>
      )}
      
      <Card className="p-6">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-stone-200">
                <th className="text-left py-3 px-2 font-semibold">Name</th>
                <th className="text-left py-3 px-2 font-semibold">Tempo</th>
                <th className="text-center py-3 px-2 font-semibold">Sequences Required</th>
                <th className="text-center py-3 px-2 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {dances.map(dance => (
                <tr key={dance.dance_id} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`dance-row-${dance.dance_id}`}>
                  <td className="py-3 px-2 font-medium">{dance.name}</td>
                  <td className="py-3 px-2 text-sm">{dance.tempo}</td>
                  <td className="py-3 px-2 text-center text-sm">{dance.sequences_required}</td>
                  <td className="py-3 px-2">
                    <div className="flex items-center justify-center gap-2">
                      <button onClick={() => handleView(dance)} className="action-icon" title="View" data-testid={`view-dance-${dance.dance_id}`}>
                        <Eye className="h-4 w-4" />
                      </button>
                      {isSuperUser && (
                        <>
                          <button onClick={() => handleEdit(dance)} className="action-icon" title="Edit" data-testid={`edit-dance-${dance.dance_id}`}>
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button onClick={() => handleDeleteClick(dance.dance_id)} className="action-icon" title="Delete" data-testid={`delete-dance-${dance.dance_id}`}>
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {dances.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              No dances found.
            </div>
          )}
        </div>
      </Card>
      
      {/* Permission Warning Dialog */}
      <Dialog open={showPermissionWarning} onOpenChange={setShowPermissionWarning}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="h-5 w-5" />
              Permission Required
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-stone-700">
              Please contact a Super User to add or modify Compulsory Dances.
            </p>
            <p className="text-sm text-stone-500 mt-2">
              Only Super Users can manage the Compulsory Dances library to ensure consistency across all competitions.
            </p>
          </div>
          <Button onClick={() => setShowPermissionWarning(false)} className="w-full">
            Understood
          </Button>
        </DialogContent>
      </Dialog>
      
      {/* Edit Dialog (Super Users only) */}
      <Dialog open={dialogOpen} onOpenChange={(val) => { setDialogOpen(val); if (!val) resetForm(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{viewOnly ? 'View Compulsory Dance' : editingItem ? 'Edit Compulsory Dance' : 'Create Compulsory Dance'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required disabled={viewOnly} data-testid="dance-name-input" />
            </div>
            <div>
              <Label>Tempo *</Label>
              <Input value={formData.tempo} onChange={(e) => setFormData({...formData, tempo: e.target.value})} required disabled={viewOnly} data-testid="dance-tempo-input" />
            </div>
            <div>
              <Label>Sequences Required</Label>
              <select className="w-full border rounded-lg px-3 py-2" value={formData.sequences_required} onChange={(e) => setFormData({...formData, sequences_required: parseInt(e.target.value)})} disabled={viewOnly} data-testid="dance-sequences-select">
                <option value={1}>1</option>
                <option value={2}>2</option>
                <option value={3}>3</option>
                <option value={4}>4</option>
              </select>
            </div>
            {!viewOnly && (
              <Button type="submit" className="w-full btn-primary" data-testid="dance-submit-button">
                {editingItem ? 'Update Dance' : 'Create Dance'}
              </Button>
            )}
          </form>
        </DialogContent>
      </Dialog>
      
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleConfirmDelete}
        itemName={itemToDelete?.name ? `"${itemToDelete.name}"` : 'this dance'}
        loading={deleting}
      />
    </>
  );
}
